import {
  Clock,
  Edit2,
  ExternalLink,
  FileText,
  Library,
  MoreHorizontal,
  Trash2,
} from 'lucide-react'
import { useMemo, useState } from 'react'
import { AuthoredIcon } from '@/components/admin/shared/authored-icon'
import { Button } from '@/components/ui/button'
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
} from '@/components/ui/dropdown-menu'
import { REVEAL_ON_HOVER } from '@/components/ui/reveal-on-hover'
import { cn } from '@/lib/utils'
import type { Task } from './types'
import { TASK_CATEGORIES } from './types'

interface TasksTableProps {
  tasks: Task[]
  loading: boolean
  onEdit: (task: Task) => void
  onDelete: (task: Task) => void
}

export function TasksTable({
  tasks,
  loading,
  onEdit,
  onDelete,
}: TasksTableProps) {
  const [sortField, setSortField] = useState<'title' | 'createdAt'>('title')
  const [sortDirection, setSortDirection] = useState<'asc' | 'desc'>('asc')

  // Sort tasks (filtering is done by parent)
  const displayedTasks = useMemo(() => {
    const sorted = [...tasks]

    sorted.sort((a, b) => {
      let comparison = 0
      if (sortField === 'title') {
        comparison = a.title.localeCompare(b.title)
      } else if (sortField === 'createdAt') {
        comparison =
          new Date(a.createdAt).getTime() - new Date(b.createdAt).getTime()
      }
      return sortDirection === 'asc' ? comparison : -comparison
    })

    return sorted
  }, [tasks, sortField, sortDirection])

  const handleSort = (field: 'title' | 'createdAt') => {
    if (sortField === field) {
      setSortDirection((d) => (d === 'asc' ? 'desc' : 'asc'))
    } else {
      setSortField(field)
      setSortDirection('asc')
    }
  }

  const getCategoryLabel = (category: string | null) => {
    if (!category) return null
    const cat = TASK_CATEGORIES.find((c) => c.value === category)
    return cat?.label || category
  }

  if (loading) {
    return (
      <div className="py-12 text-center">
        <div className="text-muted-foreground">Loading tasks...</div>
      </div>
    )
  }

  if (displayedTasks.length === 0) {
    return (
      <div className="py-12 text-center">
        <FileText size={32} className="text-muted-foreground mx-auto mb-3" />
        <p className="text-muted-foreground text-sm">
          {tasks.length === 0
            ? 'No tasks yet. Click "Add Task" to create one.'
            : 'No tasks match the current filters.'}
        </p>
      </div>
    )
  }

  return (
    <div className="overflow-x-auto">
      <table className="w-full">
        <thead className="bg-muted/30 sticky top-0">
          <tr className="text-muted-foreground text-left text-xs">
            <th className="px-4 py-3 font-medium">
              <button
                type="button"
                onClick={() => handleSort('title')}
                className="hover:text-foreground flex items-center gap-1"
              >
                Title
                {sortField === 'title' && (
                  <span>{sortDirection === 'asc' ? '↑' : '↓'}</span>
                )}
              </button>
            </th>
            <th className="px-4 py-3 font-medium">Source</th>
            <th className="px-4 py-3 font-medium">Category</th>
            <th className="px-4 py-3 font-medium">Est. Time</th>
            <th className="px-4 py-3 font-medium">Content</th>
            <th className="w-16 px-4 py-3 font-medium">Actions</th>
          </tr>
        </thead>
        <tbody className="divide-border divide-y">
          {displayedTasks.map((task) => (
            <tr
              key={task.id}
              className="hover:bg-muted/30 group transition-colors"
            >
              {/* Title */}
              <td className="px-4 py-3">
                <div className="flex items-center gap-2">
                  <span className="text-foreground font-medium">
                    {task.title}
                  </span>
                  {task.url && (
                    <a
                      href={task.url}
                      target="_blank"
                      rel="noopener noreferrer"
                      className="text-muted-foreground hover:text-primary"
                      onClick={(e) => e.stopPropagation()}
                    >
                      <ExternalLink size={14} />
                    </a>
                  )}
                </div>
                {task.notes && (
                  <p className="text-muted-foreground mt-0.5 line-clamp-1 text-sm">
                    {task.notes}
                  </p>
                )}
              </td>

              {/* Source */}
              <td className="px-4 py-3">
                {task.source ? (
                  <div className="flex items-center gap-2">
                    {task.source.icon && (
                      <AuthoredIcon
                        icon={task.source.icon}
                        size="sm"
                        className="text-muted-foreground shrink-0"
                        fallback={Library}
                      />
                    )}
                    <span className="text-sm">{task.source.name}</span>
                  </div>
                ) : (
                  <span className="text-muted-foreground text-sm">-</span>
                )}
                {task.sourceContent && (
                  <p className="text-muted-foreground mt-0.5 text-xs">
                    {task.sourceContent.title}
                  </p>
                )}
              </td>

              {/* Category */}
              <td className="px-4 py-3">
                {task.category ? (
                  <span className="bg-muted rounded px-2 py-0.5 text-xs">
                    {getCategoryLabel(task.category)}
                  </span>
                ) : (
                  <span className="text-muted-foreground text-sm">-</span>
                )}
              </td>

              {/* Est. Time */}
              <td className="px-4 py-3">
                {task.estimatedMinutes ? (
                  <div className="text-muted-foreground flex items-center gap-1 text-sm">
                    <Clock size={14} />
                    <span>{task.estimatedMinutes} min</span>
                  </div>
                ) : (
                  <span className="text-muted-foreground text-sm">-</span>
                )}
              </td>

              {/* Content Body indicator */}
              <td className="px-4 py-3">
                {task.contentBody ? (
                  <span
                    className="text-primary"
                    title="Has content body for Notion"
                  >
                    <FileText size={16} />
                  </span>
                ) : (
                  <span className="text-muted-foreground text-sm">-</span>
                )}
              </td>

              {/* Actions */}
              <td className="px-4 py-3">
                <DropdownMenu>
                  <DropdownMenuTrigger asChild>
                    <Button
                      variant="ghost"
                      size="sm"
                      className={cn(REVEAL_ON_HOVER, 'h-8 w-8 p-0')}
                    >
                      <MoreHorizontal size={16} />
                    </Button>
                  </DropdownMenuTrigger>
                  <DropdownMenuContent align="end">
                    <DropdownMenuItem onClick={() => onEdit(task)}>
                      <Edit2 size={14} className="mr-2" />
                      Edit
                    </DropdownMenuItem>
                    <DropdownMenuItem
                      onClick={() => onDelete(task)}
                      className="text-destructive focus:text-destructive"
                    >
                      <Trash2 size={14} className="mr-2" />
                      Delete
                    </DropdownMenuItem>
                  </DropdownMenuContent>
                </DropdownMenu>
              </td>
            </tr>
          ))}
        </tbody>
      </table>
    </div>
  )
}
